import { Text, Code, Label, Flex, Spinner, Divider, FormattedDateTime } from '@customerio/pluma-components/react';
import { useQueryClient } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';
import { useHydraUIAPIQuery } from '@/services/api';
import { getLayout } from '@/services/db/layout/queries';
import { layoutKeys } from '@/services/db/layout/entity';
import { isEmptyString } from '@/utils/string';
import { MetadataRow } from './metadata-row';
import { HtmlPreviewFrame } from './html-preview-frame';

type Props = { result: Extract<SearchResult, { kind: 'email_layout' }> };

type BodyPreviewProps = { isLoading: boolean; isError: boolean; html: string };

// Loading spinner / error message / rendered HTML, extracted to keep EmailLayoutPreview's
// cognitive complexity low. Callers only render this once there's something to show.
function BodyPreview({ isLoading, isError, html }: BodyPreviewProps) {
  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="200">
        <Spinner />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Text size="xs" color="subtle">
        Failed to load preview
      </Text>
    );
  }
  if (!isEmptyString(html)) {
    return <HtmlPreviewFrame html={html} title="Email layout preview" />;
  }
  return null;
}

// The search payload carries only a matched-text excerpt of a layout (the base `snippets` field),
// not its full HTML body — see EmailLayoutResult in services/auth/types.ts. So, like the template
// and snippet previews, we fetch the layout by id and render its body. The wrapper HTML (with its
// {{content}} placeholder and Liquid surfacing as literal text) renders in HtmlPreviewFrame's
// sandboxed iframe — the same component the template / in-app message previews use.
export function EmailLayoutPreview({ result }: Props) {
  const qc = useQueryClient();
  const envId = Number(result.other.env_id);
  const layoutId = result.other.id;
  const isFetchable = Number.isFinite(envId) && envId > 0 && !isEmptyString(layoutId);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: layoutKeys.detail(envId, layoutId),
    queryFn: () => getLayout(envId, layoutId, qc),
    queryConfig: { enabled: isFetchable, staleTime: 5 * 60 * 1000 },
  });

  const body = data?.body ?? '';
  const hasBody = !isEmptyString(body);
  const showPreview = isFetchable && (isLoading || isError || hasBody);

  const isArchived = result.other.archived === 'true';

  return (
    <>
      {showPreview ? (
        <>
          <BodyPreview isLoading={isLoading} isError={isError} html={body} />
          <Divider mt="100" />
        </>
      ) : null}

      <Flex direction="column" gap="100" mt={showPreview ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        {isArchived ? (
          <MetadataRow label="Status">
            <Label color="grey">Archived</Label>
          </MetadataRow>
        ) : null}
        <MetadataRow label="Created">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}
